/**
 * 3D flythrough camera path (#179 Cesium-wow) — PURE keyframe math, no Cesium.
 *
 * An operator triggers "3D flythrough" from an incident's or community's detail
 * panel: the camera descends to an oblique approach vantage, then sweeps a full
 * orbit around the site over photorealistic 3D context, then the controller
 * restores the prior view. This module computes that path as a list of
 * keyframes (lon/lat/height + heading/pitch + per-leg duration); the
 * controller (`flythrough.ts`) turns each keyframe into a `camera.flyTo`.
 *
 * The math is the small-offset equirectangular approximation (metres → degrees
 * scaled by cos(lat)), which is plenty over the ~1.5 km orbit radius — the same
 * approximation the viewshed sampler uses.
 */

export interface FlythroughTarget {
  lon: number;
  lat: number;
  /** Display label for the UI / logs (incident name, community name). */
  label?: string;
}

export interface FlythroughKeyframe {
  /** Camera position. */
  lon: number;
  lat: number;
  heightM: number;
  /** Camera orientation at the keyframe (degrees; heading 0 = north, CW). */
  headingDeg: number;
  /** Pitch (degrees; negative = looking down). */
  pitchDeg: number;
  /** Flight duration INTO this keyframe (seconds). */
  durationS: number;
}

/** Oblique approach vantage: distance back from the target + camera height. */
export const FLYTHROUGH_APPROACH_DISTANCE_M = 2_400;
export const FLYTHROUGH_APPROACH_HEIGHT_M = 1_500;
/** Orbit ring: ground radius + camera height above the target's ellipsoid 0. */
export const FLYTHROUGH_ORBIT_RADIUS_M = 1_400;
export const FLYTHROUGH_ORBIT_HEIGHT_M = 750;
/** Orbit sweep resolution: legs per full 360° revolution. More legs = a rounder
 *  orbit (each leg is one camera.flyTo great-circle hop between ring points). */
export const FLYTHROUGH_ORBIT_LEGS = 6;
/** Leg timings (seconds). */
export const FLYTHROUGH_APPROACH_DURATION_S = 3.5;
export const FLYTHROUGH_ORBIT_LEG_DURATION_S = 2.5;
/** Restore flight back to the operator's captured prior view (seconds). */
export const FLYTHROUGH_RESTORE_DURATION_S = 2.5;

const EARTH_RADIUS_M = 6_371_000;
const DEG = Math.PI / 180;

/** Offset (lon, lat) by `distanceM` along `bearingDeg` (0 = north, CW). */
export function offsetLonLat(
  lon: number,
  lat: number,
  bearingDeg: number,
  distanceM: number,
): { lon: number; lat: number } {
  const dNorth = Math.cos(bearingDeg * DEG) * distanceM;
  const dEast = Math.sin(bearingDeg * DEG) * distanceM;
  const dLat = (dNorth / EARTH_RADIUS_M) / DEG;
  const cosLat = Math.max(Math.cos(lat * DEG), 1e-6);
  const dLon = (dEast / (EARTH_RADIUS_M * cosLat)) / DEG;
  return { lon: lon + dLon, lat: lat + dLat };
}

/** Initial bearing (degrees, 0 = north, CW) from one point to another, using the
 *  same small-offset approximation (fine at orbit scale). */
export function bearingDeg(
  fromLon: number,
  fromLat: number,
  toLon: number,
  toLat: number,
): number {
  const midLat = ((fromLat + toLat) / 2) * DEG;
  const dEast = (toLon - fromLon) * DEG * EARTH_RADIUS_M * Math.cos(midLat);
  const dNorth = (toLat - fromLat) * DEG * EARTH_RADIUS_M;
  const deg = Math.atan2(dEast, dNorth) / DEG;
  return (deg + 360) % 360;
}

/** Downward pitch (degrees, negative) for a camera `heightM` up looking at a
 *  point `groundDistanceM` away on the ground. */
export function pitchToTargetDeg(heightM: number, groundDistanceM: number): number {
  return -Math.atan2(heightM, groundDistanceM) / DEG;
}

/**
 * The full flythrough path: one oblique approach keyframe, then a full orbit
 * around the target (FLYTHROUGH_ORBIT_LEGS ring points, ending back at the
 * first ring bearing). Every keyframe's heading looks AT the target and its
 * pitch puts the target in frame — so the site stays centred while the world
 * sweeps by.
 *
 * `startBearingDeg` picks where the approach comes from (default from the
 * south, looking north — terrain reads best lit from the south in Cesium's
 * default sun).
 */
export function flythroughKeyframes(
  target: FlythroughTarget,
  startBearingDeg = 180,
): FlythroughKeyframe[] {
  const frames: FlythroughKeyframe[] = [];

  // Approach: pulled back + above, looking at the site.
  const approach = offsetLonLat(
    target.lon,
    target.lat,
    startBearingDeg,
    FLYTHROUGH_APPROACH_DISTANCE_M,
  );
  frames.push({
    lon: approach.lon,
    lat: approach.lat,
    heightM: FLYTHROUGH_APPROACH_HEIGHT_M,
    headingDeg: bearingDeg(approach.lon, approach.lat, target.lon, target.lat),
    pitchDeg: pitchToTargetDeg(FLYTHROUGH_APPROACH_HEIGHT_M, FLYTHROUGH_APPROACH_DISTANCE_M),
    durationS: FLYTHROUGH_APPROACH_DURATION_S,
  });

  // Orbit ring: LEGS points swept clockwise a full revolution, ending where the
  // ring started (i + 1 .. LEGS, so the last point closes the loop).
  const orbitPitch = pitchToTargetDeg(FLYTHROUGH_ORBIT_HEIGHT_M, FLYTHROUGH_ORBIT_RADIUS_M);
  for (let i = 1; i <= FLYTHROUGH_ORBIT_LEGS; i++) {
    const ringBearing = startBearingDeg + (360 / FLYTHROUGH_ORBIT_LEGS) * i;
    const pos = offsetLonLat(target.lon, target.lat, ringBearing, FLYTHROUGH_ORBIT_RADIUS_M);
    frames.push({
      lon: pos.lon,
      lat: pos.lat,
      heightM: FLYTHROUGH_ORBIT_HEIGHT_M,
      headingDeg: bearingDeg(pos.lon, pos.lat, target.lon, target.lat),
      pitchDeg: orbitPitch,
      durationS: FLYTHROUGH_ORBIT_LEG_DURATION_S,
    });
  }

  return frames;
}

/** Total flight time of a keyframe path (seconds), excluding the restore leg. */
export function flythroughTotalDurationS(frames: ReadonlyArray<FlythroughKeyframe>): number {
  return frames.reduce((sum, f) => sum + f.durationS, 0);
}
